import {
  UseInfiniteQueryOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";

import analytics from "@/services/analytics";
import {
  PaginatedSearch,
  SearchStoresOrProductsResponse,
  searchStoreProducts,
  searchStoresOrProducts,
} from "@/services/api/search";
import { logger } from "@/services/logger";
import { ADD_GLOBAL_SEARCH, ADD_STORE_SEARCH } from "@/store/ducks/lastSearches";

import { useAppDispatch } from "../application/store";

export const useGlobalSearch = ({
  query,
  limit = 200,
  options,
}: {
  query: string | null;
  limit?: number;
  options?: UseQueryOptions<SearchStoresOrProductsResponse>;
}) => {
  const dispatch = useAppDispatch();
  const { data, ...rest } = useQuery<SearchStoresOrProductsResponse>({
    queryKey: ["global-search", query],
    queryFn: () => searchStoresOrProducts(query?.trim(), limit),
    ...options,
    onSuccess: (data) => {
      analytics.logEvent("search_products_or_vendors", {
        query,
      });
      if (data?.produtos?.length) {
        dispatch(ADD_GLOBAL_SEARCH(query));
      }
      if (options?.onSuccess) {
        options?.onSuccess(data);
      }
    },
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });

  return {
    data: !query ? null : data,
    ...rest,
  };
};

export const useInStoreSearch = ({
  query,
  storeId,
  limit = 10,
  options,
}: {
  query: string | null;
  storeId: string;
  limit?: number;
  options?: UseInfiniteQueryOptions<PaginatedSearch>;
}) => {
  const dispatch = useAppDispatch();
  const { data, ...rest } = useInfiniteQuery<PaginatedSearch>({
    queryKey: ["in-store-search", { query, storeId }],
    queryFn: ({ pageParam = 1 }) =>
      searchStoreProducts({
        query: query?.trim(),
        page: pageParam,
        storeId,
        limit,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage?.page < lastPage?.pages) {
        return lastPage.page + 1;
      }
    },
    ...options,
    onSuccess: (data) => {
      analytics.logEvent("search_products_in_selected_vendor", {
        query,
        storeId,
      });
      if (data?.pages[0]?.produtos?.length) {
        dispatch(
          ADD_STORE_SEARCH({
            query,
            storeId,
          })
        );
      }
      if (options?.onSuccess) {
        options?.onSuccess(data);
      }
    },
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });
  return {
    data: !query ? [] : data?.pages.map((page) => page.produtos).flat(),
    total: data?.pages[0]?.total,
    ...rest,
  };
};
